import { FC, ReactNode } from 'react'

import { routes } from 'commons/constants/routes'
import { UsPhoneLabel } from 'ui/components'
import { Button } from 'ui/components/Buttons'
import { DateTime } from 'ui/components/DateTime'
import { Hr } from 'ui/components/Hr'
import { Info, InfoSet, InfoSetDirection } from 'ui/components/Info'
import { Link } from 'ui/components/Link'
import { PageSection, PageSectionFace } from 'ui/components/Page'
import { DotLabelBadge } from 'ui/temp/DotLabelBadge'
import { Col, Col2x, Row } from 'ui/themes/globalStyles'

import { LicenseDetailsProps } from './LicenseDetailsProps'
import { CRBMonitorInfo } from './components/CRBMonitorInfo'

export const LicenseDetails: FC<LicenseDetailsProps> = (props) => {
  const { data } = props
  const {
    id,
    active,
    name,
    relationship,
    number,
    issueDate,
    type,
    subType,
    posType,
    showPosTypeOther,
    phone,
    taxRates,
    address,
    createdAt,
    updatedAt,
    APIConnection,
    showCRBConnectionStatus,
    showCRBConnectionMark,
    showNullificationDate = undefined,
    internalNotes
  } = data
  const { cityTax, MJRetailTax, specialTax, stateTax, countyTax } = taxRates
  const { street, postalCode, state, city } = address

  const licenseDetailsRows: Array<[ReactNode | null, ReactNode | null]> = [
    [<Info label="Name">{name}</Info>, <Info label="License Number">{number}</Info>],
    [
      <Info label="Relationship">
        <Link to={routes.bank.relationships.details} params={{ id: relationship.id.toString() }}>
          {relationship.name}
        </Link>
      </Info>,
      <Info label="Issue Date">
        <DateTime date={issueDate} />
      </Info>
    ],
    [
      <Info label="Active">
        <DotLabelBadge label="" dotActive={active} />
      </Info>,
      showNullificationDate && (
        <Info label="Nullification Date">
          <DateTime date={showNullificationDate} />
        </Info>
      )
    ],
    [<Info label="Type">{type}</Info>, <Info label="Subtype">{subType}</Info>],
    [
      <Info label="Phone">
        <UsPhoneLabel value={phone} />
      </Info>,
      <Info label="POS Type">{posType}</Info>
    ],
    [
      <Info label="API Connection">
        <DotLabelBadge label={APIConnection} dotActive={APIConnection === 'Active'} />
      </Info>,
      null
    ]
  ]

  if (showPosTypeOther?.length > 0) {
    licenseDetailsRows.push([<Info label="POS Type Other">{showPosTypeOther}</Info>, null])
  }

  if (showCRBConnectionMark && showCRBConnectionStatus) {
    licenseDetailsRows.push([
      <CRBMonitorInfo
        showCRBConnectionMark={showCRBConnectionMark}
        showCRBConnectionStatus={showCRBConnectionStatus}
      />,
      null
    ])
  }

  return (
    <PageSection
      face={PageSectionFace.SECONDARY}
      title="License Information"
      actions={
        <Link to="/licenses/$licenseId/edit" params={{ licenseId: id.toString() }}>
          <Button face="primary">Edit</Button>
        </Link>
      }
      anchor="general"
    >
      <Hr />
      <InfoSet direction={InfoSetDirection.ROW} legend="License Details">
        {licenseDetailsRows
          .filter(([left, right]) => left || right)
          .map(([left, right], index) => (
            <Row key={index}>
              <Col>{left}</Col>
              <Col>{right}</Col>
            </Row>
          ))}
        <Row>
          <Col2x>
            <Info label="Notes">{internalNotes}</Info>
          </Col2x>
        </Row>
      </InfoSet>
      <Hr />
      <InfoSet direction={InfoSetDirection.ROW} legend="Tax rates for License">
        <Col>
          <Info label="City Tax">{`${cityTax || '---'} %`}</Info>
          <Info label="MJ Retail Tax">{`${MJRetailTax || '---'} %`}</Info>
          <Info label="Special Tax">{`${specialTax || '---'} %`}</Info>
        </Col>
        <Col>
          <Info label="County Tax">{`${countyTax || '---'} %`}</Info>
          <Info label="State Tax">{`${stateTax || '---'} %`}</Info>
        </Col>
      </InfoSet>
      <Hr />
      <InfoSet direction={InfoSetDirection.ROW} legend="Address Information">
        <Col>
          <Info label="Address Information">{street}</Info>
          <Info label="Zip Code">{postalCode}</Info>
        </Col>
        <Col>
          <Info label="State">{state}</Info>
          <Info label="City">{city}</Info>
        </Col>
      </InfoSet>
      <Hr />
      <InfoSet direction={InfoSetDirection.ROW} legend="System Information">
        <Col>
          <Info label="Created At">
            <DateTime date={createdAt} withTime />
          </Info>
        </Col>
        <Col>
          <Info label="Last Modified At">
            <DateTime date={updatedAt} withTime />
          </Info>
        </Col>
      </InfoSet>
    </PageSection>
  )
}
